// ════════════════════════════════════════════════════════════════════════════
//  features/fiscal/transporte/hooks/useAverbacao.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { averbacaoService, type FiltrosAverbacao } from '../services/averbacaoService';
import type { AverbarRequest } from '../types';

const KEYS = {
  all:      ['averbacao'] as const,
  historico: (p: number, t: number, f: FiltrosAverbacao) => ['averbacao', 'historico', p, t, f] as const,
};

export const useAverbacao = (pagina = 1, tamanhoPagina = 20, filtros: FiltrosAverbacao = {}) => {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: KEYS.historico(pagina, tamanhoPagina, filtros),
    queryFn:  () => averbacaoService.historico(pagina, tamanhoPagina, filtros),
    placeholderData: keepPreviousData,
  });
  const itens        = data?.itens ?? [];
  const total        = data?.total ?? 0;
  const totalPaginas = Math.max(1, data?.totalPaginas ?? 1);

  const averbar = useMutation({
    mutationFn: (req: AverbarRequest) => averbacaoService.averbar(req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) toast.success(`Averbado — Protocolo: ${res.protocolo ?? '-'}`);
      else toast.warning(`Erro na averbação: ${res.mensagemErro ?? 'desconhecido'}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cancelar = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) => averbacaoService.cancelar(id, motivo),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) toast.success('Averbação cancelada com sucesso');
      else toast.warning(`Erro ao cancelar: ${res.mensagemErro ?? 'desconhecido'}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return { itens, total, totalPaginas, isLoading, averbar, cancelar };
};
